import { FileType } from "@/enums/FileType";

/**
 * Returns the {@link FileType} enum, based on either the MIME-type, or the extension.
 *
 * @param fileOrMimeType - The file or MIME-type to get the {@link FileType} for.
 * @returns The {@link FileType} of the file.
 */
export function getFileType(
	fileOrMimeType?:
		| { contentType?: string | null; filename: string }
		| string
		| null,
): FileType | undefined {
	if (fileOrMimeType === undefined || fileOrMimeType === null) {
		return undefined;
	}

	let fileType = FileType.Default;

	const mimeType =
		typeof fileOrMimeType === "string"
			? fileOrMimeType
			: fileOrMimeType.contentType;

	// Infer filetype based on MIME-type
	if (mimeType) {
		switch (true) {
			case "image/svg+xml" === mimeType:
				fileType = FileType.Svg;
				break;
			case /image\//.test(mimeType):
				// Catches all MIME-types starting with 'image/'
				fileType = FileType.Img;
				break;
			case "application/vnd.ms-excel" === mimeType:
			case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ===
				mimeType:
			case "text/csv" === mimeType:
			case "application/vnd.oasis.opendocument.spreadsheet" === mimeType:
				fileType = FileType.Sheet;
				break;
			case "application/pdf" === mimeType:
				fileType = FileType.Pdf;
				break;
		}
	}

	if (fileType !== FileType.Default || typeof fileOrMimeType === "string") {
		return fileType;
	}

	const file = fileOrMimeType;
	const fileExtension = file.filename.split(".").pop()?.toLowerCase();

	if (!fileExtension) {
		return undefined;
	}

	if (["svg", "ai", "eps"].includes(fileExtension)) {
		fileType = FileType.Svg;
	} else if (
		["png", "jpg", "jpeg", "gif", "webp", "tiff", "raw"].includes(fileExtension)
	) {
		fileType = FileType.Img;
	} else if (
		["xlsx", "xls", "xlsm", "numbers", "ots", "xlsb", "csv"].includes(
			fileExtension,
		)
	) {
		fileType = FileType.Sheet;
	} else if (
		file.contentType?.endsWith("/pdf") ||
		["pdf"].includes(fileExtension)
	) {
		fileType = FileType.Pdf;
	}

	return fileType;
}

/**
 * Retrieve an icon for a file, based on it's type or data.
 *
 * @param fileType - The {@link FileType} to get the icon for.
 * @param fileData - When specified, the icon is generated based on {@link File | fileData}.
 * @returns The icon for the file
 */
export function getFileIcon(fileType?: FileType | null, fileData?: File) {
	if (fileData) {
		return URL.createObjectURL(fileData);
	}

	return getImageUrl(fileType);
}

/**
 * Get the url to an image, based on the name. Can be used as src parameter of an HTML image.
 *
 * @param name - The name of the image.
 * @returns The url to the image.
 */
export function getImageUrl(name?: string | null) {
	if (!name) {
		return undefined;
	}

	const url = new URL(`/src/assets/${name}`, import.meta.url);

	if (url.pathname.endsWith("/undefined")) {
		return undefined;
	}

	return url.href;
}

/**
 * Returns the filename specified in a local path, without the extension.
 *
 * @param path - The filepath.
 * @returns The filename, without extension.
 */
export function nameFromLocalPath(path: string) {
	// https://stackoverflow.com/a/3820412
	let base = new String(path).substring(path.lastIndexOf("/") + 1);
	if (base.lastIndexOf(".") != -1)
		base = base.substring(0, base.lastIndexOf("."));
	return base;
}

export const acceptedMimeTypes = [
	"image/png",
	"image/webp",
	"image/gif",
	"image/svg+xml",
	"image/avif",
	"image/heic",
	"image/heif",
	"application/pdf",
	"image/jpeg",
	"image/tiff",
];
